import React from 'react';
import { AccessCode, Loader, Table } from '../../../../../components';
import { Edit } from './ClassesTable.components';
import { createGlobalStyle } from 'styled-components';
import type { NewClassGet, RosterManagementStudentListItem } from '@whizz/api-model';
import type { Column } from 'react-table';
import type { EditMutationProps } from './AddEditClassModal';
import { useI18n } from '@whizz/react-i18n';
import { Groups, YearGroup } from '../../Students/modules/StudentsTable.components';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_id'],
    [data-id='col_studentCount'] {
      width: 0;
    }
    [data-id='col_name'],
    [data-column='cell_name'],
    [data-id='col_accessCode'],
    [data-column='cell_accessCode'],
    [data-id='col_yearGroupId'],
    [data-column='cell_yearGroupId'] {
      width: 33.33%;
    }
    [data-column='cell_studentCount'] {
      text-align: center;
    }

    @media (min-width: 640px) {
      [data-id='col_name'],
      [data-column='cell_name'],
      [data-id='col_yearGroupId'],
      [data-column='cell_yearGroupId'],
      [data-id='col_accessCode'],
      [data-column='cell_accessCode'] {
        padding-left: 8% !important;
      }
    }
  }
`;

interface Props {
  onEdit: (data: EditMutationProps) => void;
  classes: NewClassGet,
  isLoading: boolean;
}

const ClassesTable = ({ onEdit, classes, isLoading }: Props): JSX.Element => {
  const { translate } = useI18n();

  const columns: Column<ClassTableItem>[] = [
    {
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => (
        <Edit
          onClick={() => onEdit({
            id: row.original.id,
            name: row.original.name,
            yearGroupId: row.original.yearGroupId,
            color: row.original.color,
          })}
        />
      ),
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.CLASSES.TABLE.CLASS') as string,
      accessor: 'name',
      Cell: ({ row }) => <Groups groups={[ { ...row.original } ] as RosterManagementStudentListItem['classes']} />,
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.CLASSES.TABLE.STUDENT_COUNT') as string,
      accessor: 'studentCount',
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.CLASSES.TABLE.YEAR_GROUP') as string,
      accessor: 'yearGroupId',
      Cell: ({ value }: { value: number }) => <YearGroup yearGroupId={value} />,
    },
    {
      Header: translate('pages.ROSTER_MANAGEMENT.CLASSES.TABLE.ACCESS_CODE') as string,
      accessor: 'accessCode',
      Cell: ({ value }: { value: string }) => <AccessCode code={value} />,
    },
  ];

  if (isLoading) {
    return (<Loader />);
  }

  return (
    <>
      <GlobalStyle />
      <Table
        id={'classes'}
        cols={columns}
        content={classes}
        initialState={{
          sortBy: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
        disableSortRemove
      />
    </>
  );
};

export default ClassesTable;


interface ClassTableItem {
  id: number;
  name: string;
  yearGroupId: number | null,
  color: number,
  studentCount: number,
  accessCode: string,
}
